import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { FileText } from 'lucide-react';

export function CreateEncounterDialog({
  open,
  onOpenChange,
  language,
  templates,
  newEncounter,
  onEncounterChange,
  onCreate
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-cinzel">
            {language === 'fr' ? 'Nouvelle rencontre' : 'New encounter'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {/* Template Selection */}
          {templates.length > 0 && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                {language === 'fr' ? 'Depuis un modèle' : 'From template'}
              </Label>
              <Select 
                value={newEncounter.from_template_id || 'none'} 
                onValueChange={(v) => {
                  const templateId = v === 'none' ? '' : v;
                  const template = templates.find(t => t.id === templateId);
                  onEncounterChange({ 
                    ...newEncounter, 
                    from_template_id: templateId,
                    name: template ? template.name.replace('Template: ', '') : newEncounter.name,
                    description: template ? template.description : newEncounter.description
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={language === 'fr' ? 'Aucun modèle' : 'No template'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{language === 'fr' ? 'Aucun modèle' : 'No template'}</SelectItem>
                  {templates.map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      <span className="flex items-center gap-2">
                        {t.name}
                        <span className="text-xs text-muted-foreground">
                          ({t.creature_count} {language === 'fr' ? 'créatures' : 'creatures'}, {t.total_xp} XP)
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {newEncounter.from_template_id && (
                <p className="text-xs text-muted-foreground">
                  {language === 'fr' 
                    ? 'Les combattants du modèle seront automatiquement ajoutés'
                    : 'Template combatants will be automatically added'}
                </p>
              )}
            </div>
          )}
          
          <div className="space-y-2">
            <Label>{language === 'fr' ? 'Nom' : 'Name'}</Label>
            <Input
              value={newEncounter.name}
              onChange={(e) => onEncounterChange({ ...newEncounter, name: e.target.value })}
              placeholder={language === 'fr' ? 'Embuscade gobeline' : 'Goblin ambush'}
              data-testid="encounter-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label>{language === 'fr' ? 'Lieu' : 'Location'}</Label>
            <Input
              value={newEncounter.location}
              onChange={(e) => onEncounterChange({ ...newEncounter, location: e.target.value })}
              placeholder={language === 'fr' ? 'Forêt sombre' : 'Dark forest'}
            />
          </div>
          <div className="space-y-2">
            <Label>{language === 'fr' ? 'Description' : 'Description'}</Label>
            <Textarea
              value={newEncounter.description}
              onChange={(e) => onEncounterChange({ ...newEncounter, description: e.target.value })}
              rows={2}
            />
          </div>
          
          {/* Combat Settings */}
          <div className="border-t pt-4 space-y-3">
            <Label className="text-sm font-semibold">{language === 'fr' ? 'Paramètres' : 'Settings'}</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">{language === 'fr' ? 'Affichage PV (joueurs)' : 'HP Display (players)'}</Label>
                <Select 
                  value={newEncounter.hp_display_mode} 
                  onValueChange={(v) => onEncounterChange({ ...newEncounter, hp_display_mode: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="exact">{language === 'fr' ? 'Valeur exacte' : 'Exact'}</SelectItem>
                    <SelectItem value="status">{language === 'fr' ? 'État' : 'Status'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 pt-4">
                <Switch
                  checked={newEncounter.auto_reroll_initiative}
                  onCheckedChange={(v) => onEncounterChange({ ...newEncounter, auto_reroll_initiative: v })}
                />
                <Label className="text-xs">
                  {language === 'fr' ? 'Relance initiative auto' : 'Auto re-roll init.'}
                </Label>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {language === 'fr' ? 'Annuler' : 'Cancel'}
          </Button>
          <Button onClick={onCreate} disabled={!newEncounter.name} data-testid="create-encounter-submit">
            {language === 'fr' ? 'Créer' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
